import { InlineFlex } from '@components/Layout/Blocks';
import { AutoCol, Col } from '@components/Layout/Col';
import { Row } from '@components/Layout/Row';
import { NothingToShow } from '@components/NothingToShow';
import { SkuSelector } from '@containers/Apparel/Skus/components/SkuSelector';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { createDialog, DialogActions, DialogContent, DialogHeader, DialogLoading } from '@packages/dialog';
import { call } from '@packages/functions';
import { createMemo } from '@packages/react';
import { find } from 'lodash';
import { useState } from 'react';

const useStyles = makeStyles(() => ({
    section: {
        margin: '0 0 14px',
        width: 'auto',
        border: '1px solid #ccc',
        borderRadius: '2px 2px 0 0',
    },

    sectionHeader: {
        backgroundColor: '#f5f5f5',
        padding: '12px 20px !important',
    },

    sectionContent: {
        borderTop: '1px solid #ccc',
    },

    collapse: {
        width: '100%',
    },
    packageTitle: {
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.5px',
        padding: '16px 0 16px 2px',
        width: 'calc( 100% - 32px)',
    },
    footerButtons: {
        width: '183px',
        height: '48px',
        marginRight: '3px',
        borderRadius: '28px',
        boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.2)',
    },
}));

const Content = createMemo<any>(
    ({ item, onItemChange, api }) => {
        const packageObject: any = { ...(item ? item.value : {}) };
        const classes = useStyles();
        const { category } = packageObject;
        const subcategories =
            packageObject.subcategories?.reduce((items, subcategory) => {
                const { subcategory_to_repeat } = subcategory;
                for (let i = 0; i < subcategory_to_repeat; i++) {
                    items.push(subcategory);
                }
                return items;
            }, []) || [];
        const [states, setStates] = useState([...subcategories].map((_, id) => id === 0));

        const defaultSkus = [...subcategories].map(({ id, default_sku, subcategory, item_id = null }) => ({
            subcategory_id: id,
            sku_id: default_sku.id,
            sku: default_sku,
            subcategory,
            item_id,
        }));

        const [loading, setLoading] = useState<boolean[]>(() => [...subcategories].map(() => false));
        const [errors, setErrors] = useState<boolean[]>(() => [...subcategories].map(() => false));
        const [skus, setSkus] = useState<
            {
                subcategory_id: number;
                subcategory: any;
                sku_id: number;
                sku: any;
                item_id?: number;
                is_edited?: boolean;
            }[]
        >(() => [...defaultSkus]);
        const handleLoading = (idx) => (state) => {
            const newLoadingState = [...loading];
            newLoadingState[idx] = state;
            setLoading(newLoadingState);
        };

        const handleSelect = (idx, sku_id) => (newId, _, list) => {
            const newSkus = [...skus];
            const sku = find(list, ['id', newId]);
            newSkus[idx].sku_id = newId;
            newSkus[idx].is_edited = newSkus[idx].sku_id !== sku_id;
            newSkus[idx].sku = sku;

            setSkus(newSkus);
        };

        const handleSubmit = () => {
            call(onItemChange, packageObject, skus);
            api.cancel();
        };

        const handleSkuData = (idx) => (data, err) => {
            errors[idx] = !data || data.length === 0 || !!err;
            setErrors([...errors]);
        };

        const handleOpen = (idx) => () => {
            const newStates = [...states];
            newStates[idx] = !newStates[idx];
            setStates(newStates);
        };

        let content = subcategories
            .filter((s) => s)
            .map(({ id, sku_id, default_sku, name }, idx) => (
                // Map each subcategory and create an SkuSelector for it
                <SkuSelector
                    key={`${idx}_${id}`}
                    onLoading={handleLoading(idx)}
                    onSelect={handleSelect(idx, sku_id)}
                    onData={handleSkuData(idx)}
                    multiple={false}
                    plain
                    categoryId={category.id}
                    subCategoryId={id}
                    packageId={packageObject.id}
                    value={default_sku.id}
                    showCustomizableWheel
                    isPackage
                >
                    {(children) => (
                        <Row className={classes.section}>
                            <ButtonBase
                                component={Col}
                                xs={12}
                                className={classes.sectionHeader}
                                align={'center'}
                                onClick={handleOpen(idx)}
                            >
                                <InlineFlex flex={'1 1 auto'}>
                                    <Typography>{`Select a ${name.toLowerCase()} style.`}</Typography>
                                </InlineFlex>

                                <InlineFlex>
                                    {states[idx] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </InlineFlex>
                            </ButtonBase>
                            <Collapse in={states[idx]} className={classes.collapse} unmountOnExit>
                                <Col xs={12} padding={'6px 6px 10px 6px'} className={classes.sectionContent}>
                                    {children}
                                    <Divider />
                                </Col>
                            </Collapse>
                        </Row>
                    )}
                </SkuSelector>
            ));

        const hasNoContent = errors.filter((e) => e).length === errors.length;

        if (hasNoContent) {
            content = <NothingToShow title={'No extra options.'} text={"There's no extra option yet."} />;
        }

        return (
            <>
                <DialogHeader padding={'0'}>
                    <AutoCol xs justify={'space-between'}>
                        <Typography className={classes.packageTitle}>{item.value.name}</Typography>
                        <IconButton style={{ padding: '16px 7px 16px 0' }} onClick={api.cancel}>
                            <CloseIcon />
                        </IconButton>
                    </AutoCol>
                </DialogHeader>

                <Divider />

                <DialogContent style={{ padding: '17px 17px 0 17px' }}>{content}</DialogContent>

                <Divider />

                <DialogActions style={{ padding: '8px 16px', justifyContent: 'center' }}>
                    <Button
                        style={{ backgroundColor: '#F2F2F2' }}
                        className={classes.footerButtons}
                        onClick={api.cancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        style={{ backgroundColor: '#e4002b', color: 'white' }}
                        className={classes.footerButtons}
                        onClick={handleSubmit}
                        disabled={hasNoContent}
                    >
                        Save changes
                    </Button>
                </DialogActions>

                <DialogLoading loading={loading.filter((s) => s).length > 0} />
            </>
        );
    },
    {
        displayName: 'SummaryEditPackageDialogContent',
    },
);

export const SummaryEditPackageDialog = createDialog<any>({
    width: '100%',
    margin: 0,
    maxWidth: 1000,
    noToggle: true,
    dataProps: ['item', 'onItemChange'],
})(Content);
